import moment from 'moment';

export const REQUIRED_FIELDS = [
  'date',
  'market',
  'id',
  'route',
  'stop',
  'stockingIndex',
  'stopType',
];

export const OPTIONAL_TIME_FIELDS = [
  'calculatedStartTime',
  'calculatedEndTime',
];

// Mirrors the DriverStopType schema enum.
export const VALID_STOP_TYPES = [
  'STOCKING',
  'CLEAROUT',
  'CLEAROUT_DECOMMISSION',
];

// Region codes the dashboards' Region filter matches exactly.
export const VALID_MARKETS = ['MW', 'NE', 'WC', 'S'];

// Optional CSV columns the upload understands, beyond REQUIRED_FIELDS.
export const OPTIONAL_COLUMNS = [
  'calculatedStartTime',
  'calculatedEndTime',
  'isStripeStop',
  'nextmvRunId',
  'expectedDropoffDate',
];

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

export const isRowEmpty = (row) => Object.values(row).every(isBlank);

// Validates one parsed CSV row. `locationIds` = Set of LIVE location ids,
// or null while loading (skips the existence check).
export const validateLine = (item, locationIds) => {
  const errors = [];

  REQUIRED_FIELDS.forEach((field) => {
    if (isBlank(item[field])) {
      errors.push(`Missing required value for "${field}"`);
    }
  });

  if (
    !isBlank(item.date) &&
    !moment(String(item.date), DATE_FORMAT, true).isValid()
  ) {
    errors.push(`date "${item.date}" is not in ${DATE_FORMAT} format`);
  }

  if (
    !isBlank(item.expectedDropoffDate) &&
    !moment(String(item.expectedDropoffDate), DATE_FORMAT, true).isValid()
  ) {
    errors.push(
      `expectedDropoffDate "${item.expectedDropoffDate}" is not in ` +
        `${DATE_FORMAT} format`,
    );
  }

  ['stop', 'stockingIndex'].forEach((field) => {
    if (!isBlank(item[field]) && !Number.isInteger(item[field])) {
      errors.push(`${field} "${item[field]}" must be a whole number`);
    }
  });

  if (
    !isBlank(item.stopType) &&
    !VALID_STOP_TYPES.includes(String(item.stopType).toUpperCase())
  ) {
    errors.push(
      `stopType "${item.stopType}" must be one of ${VALID_STOP_TYPES.join(
        ', ',
      )}`,
    );
  }

  if (
    !isBlank(item.id) &&
    locationIds !== null &&
    !locationIds.has(String(item.id))
  ) {
    errors.push(`Location id "${item.id}" is not a LIVE location`);
  }

  if (
    !isBlank(item.market) &&
    !VALID_MARKETS.includes(String(item.market).trim())
  ) {
    errors.push(
      `market "${item.market}" must be one of ${VALID_MARKETS.join(', ')}`,
    );
  }

  OPTIONAL_TIME_FIELDS.forEach((field) => {
    if (
      !isBlank(item[field]) &&
      !moment(String(item[field]), DATETIME_FORMAT, true).isValid()
    ) {
      errors.push(
        `${field} "${item[field]}" is not in ${DATETIME_FORMAT} format`,
      );
    }
  });

  if (!isBlank(item.isStripeStop)) {
    const flag = String(item.isStripeStop).toLowerCase();
    if (flag !== 'true' && flag !== 'false') {
      errors.push(`isStripeStop "${item.isStripeStop}" must be true or false`);
    }
  }

  return { status: errors.length > 0 ? -1 : 1, errors };
};

// Duplicate = same date + route + id + stockingIndex as another row.
const duplicateKey = (row) =>
  [row.date, row.route, row.id, row.stockingIndex]
    .map((value) => String(value).trim().toLowerCase())
    .join('|');

// Per-row rules plus the cross-row duplicate check. Returns an array
// parallel to `rows`; duplicate messages use CSV line numbers (header = 1).
export const validateRows = (rows, locationIds) => {
  const results = rows.map((row) => validateLine(row, locationIds));

  const groups = {};
  rows.forEach((row, index) => {
    if ([row.date, row.route, row.id, row.stockingIndex].some(isBlank)) {
      return;
    }
    const key = duplicateKey(row);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(index);
  });

  Object.values(groups).forEach((indexes) => {
    if (indexes.length < 2) {
      return;
    }
    indexes.forEach((rowIndex) => {
      const otherLines = indexes
        .filter((index) => index !== rowIndex)
        .map((index) => index + 2);
      results[rowIndex].status = -1;
      results[rowIndex].errors.push(
        `Duplicate stop: same date, route, id and stockingIndex as line${
          otherLines.length > 1 ? 's' : ''
        } ${otherLines.join(', ')}`,
      );
    });
  });

  return results;
};

// Headers the upload doesn't recognize (typo'd columns are otherwise
// silently ignored).
export const unknownColumns = (fields) => {
  const known = new Set([...REQUIRED_FIELDS, ...OPTIONAL_COLUMNS]);
  return (fields || []).filter(
    (field) => !isBlank(field) && !known.has(String(field).trim()),
  );
};

// Route names with no _am_/_pm_ token — the Monitoring shift filter can't
// classify them. Local regex (getRouteShift lives in a Leaflet-importing
// module). Warning only.
const SHIFT_TOKEN = /(?:^|_)(am|pm)(?:_|$)/i;
export const routesMissingShiftToken = (rows) => {
  const missing = new Set();
  (rows || []).forEach((row) => {
    if (isBlank(row.route)) {
      return;
    }
    const route = String(row.route).trim();
    if (!SHIFT_TOKEN.test(route)) {
      missing.add(route.toLowerCase());
    }
  });
  return [...missing].sort((first, second) => first.localeCompare(second));
};

// CSV lines (header = 1) with an inverted time window. Warn-only.
export const linesWithInvertedWindow = (rows) =>
  (rows || [])
    .map((row, index) => {
      if (isBlank(row.calculatedStartTime) || isBlank(row.calculatedEndTime)) {
        return null;
      }
      const start = moment(
        String(row.calculatedStartTime),
        DATETIME_FORMAT,
        true,
      );
      const end = moment(String(row.calculatedEndTime), DATETIME_FORMAT, true);
      return start.isValid() && end.isValid() && start.isAfter(end)
        ? index + 2
        : null;
    })
    .filter((line) => line !== null);
